import { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { GlideIcon } from "@glide/common";
import * as React from "react";
import "twin.macro";

type Option = { value: string; label: string };

interface MultiSelectDropdownProps {
    readonly initialSelected: Option[];
    readonly options: Option[];
    readonly onSelect: (selectedItems: Option[]) => void;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = p => {
    const { options, onSelect, initialSelected } = p;
    const [selected, setSelected] = useState<Option[]>(initialSelected);
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    const toggle = React.useCallback(
        (value: Option) => {
            const newSelection = selected.some(opt => opt.value === value.value)
                ? selected.filter(opt => opt.value !== value.value)
                : [...selected, value];

            setSelected(newSelection);
            onSelect(newSelection);
        },
        [onSelect, selected]
    );
    const label = React.useMemo(() => {
        return selected.length > 0 ? `${selected.length} selected` : "Select options…";
    }, [selected]);

    const isSelected = React.useCallback(
        (option: Option) => {
            return selected.some(selectedItem => selectedItem.value === option.value);
        },
        [selected]
    );

    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
                <button
                    ref={buttonRef}
                    tw="bg-n200A! hocus:bg-n300A! focus:(ring-1 ring-text-accent) text-builder-base min-w-[192px] px-2.5 flex items-center justify-between h-8 rounded-lg text-text-dark w-full"
                    aria-label="Select options">
                    {label}
                    <GlideIcon kind="stroke" icon="st-caret" iconSize={16} tw="text-icon-base mr-px" />
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
                style={{ width: buttonRef.current?.offsetWidth ?? "auto" }}
                side="bottom"
                align="start"
                tw="flex flex-col bg-bg-front [max-height:calc(50vh - 120px)] overflow-y-auto rounded-lg shadow-lg-dark overflow-hidden min-w-[192px] w-full z-10 py-1">
                {options.map(opt => (
                    <DropdownMenu.CheckboxItem
                        key={opt.value}
                        tw="w-full text-builder-sm text-text-base font-normal px-2.5 py-1.5 not-last:mb-px select-none flex
                        items-center justify-between
                        hocus:(cursor-pointer bg-n100A)"
                        checked={isSelected(opt)}
                        onSelect={e => {
                            // prevent the menu from closing on click
                            e.preventDefault();
                        }}
                        onCheckedChange={() => toggle(opt)}>
                        {opt.label}
                        <DropdownMenu.ItemIndicator>
                            <GlideIcon kind="stroke" icon="st-checkmark" iconSize={16} tw="text-text-accent" />
                        </DropdownMenu.ItemIndicator>
                    </DropdownMenu.CheckboxItem>
                ))}
            </DropdownMenu.Content>
        </DropdownMenu.Root>
    );
};
